import {useNavigate} from "react-router-dom";
import {Autocomplete, Box, Chip, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {OrganizationService} from "crqlar-react-common/dist/services/identity/organization.service";
import {Organization} from "crqlar-react-common/dist/models/organization/organization";
import ImageUploadField from "crqlar-react-common/dist/components/ImageUploadField";
import Property from "crqlar-react-common/dist/components/Property";
import {getNameForLanguage, ISO_LANGS} from "crqlar-react-common/dist/utils/translations.util";
import {CURRENCY_CODE_SYMBOLS, formatCurrencyNameSymbol} from "crqlar-react-common/dist/utils/currency.util";
import AddressField from "crqlar-react-common/dist/components/AddressField";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useDispatch} from "react-redux";
import {AuthService} from "crqlar-react-common/dist/services/auth/auth.service";
import {AuthActions} from "crqlar-react-common/dist/state/auth.reducer";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import OrganizationUrlsField from "crqlar-react-common/dist/components/OrganizationUrlsField";

const OrganizationDetails = () => {

    useTrackScreenVisit('system organization details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const organizationData = useEntity({
        get: async () => {
            return await OrganizationService.getCurrent()
        },
        update: OrganizationService.update,
        create: OrganizationService.update,
        onSaved: async (organization, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/organizations/${organization.id}`)
            }
            const response = await AuthService.validateToken()
            if (response?.token) {
                localStorage.setItem('token', response.token)
                dispatch({
                    type: AuthActions.SET_USER,
                    payload: {
                        user: response.user,
                        organization: response.organization
                    }
                })
            }
        },
        isValid: (organization) => {
            return !!organization.name
        },
        cleanupBeforeSave: (organization) => {
            return {
                ...organization,
                name: organization.name?.trim(),
                legalName: organization.legalName?.trim(),
                primaryEmail: organization.primaryEmail?.trim(),
                guestContactPhone: organization.guestContactPhone?.trim(),
                vatId: organization.vatId?.trim(),
                commercialRegisterNumber: organization.commercialRegisterNumber?.trim(),
                iban: organization.iban?.trim(),
                bic: organization.bic?.trim()
            }
        }
    })

    const organization = organizationData.item
    const setOrganization = organizationData.setItem
    const nameMissing = isRequiredTextMissing(organizationData, organization.name)


    //endregion Sate

    //region Handlers

    const updateField = (field: keyof Organization, value: any) => {
        setOrganization({...organization, [field]: value})
    }

    //endregion Handlers

    //region UI

    const sectionTitleSx = {
        fontSize: {xs: '18px', sm: '20px'},
        lineHeight: 1.25,
    }

    const fieldGroupGap = {xs: 1.5, sm: 1}

    const sectionDividerSx = {
        display: {xs: 'none', sm: 'block'},
    }

    const headerSection = (
        <SettingsEntityHeader
            title={organization.name}
            data={organizationData}
            back={() => navigate('/system')}
            helpUrl={'https://crqlar.notion.site/Was-ist-eine-Organisation-36f9e2a69b99810db411dd50d920639d?pvs=74'}
        />
    )

    const titleSection = (
        <Stack gap={2}>
            <Stack gap={1} alignItems={'flex-start'}>
                <Typography variant={'subtitle1'} textAlign={'start'}>
                    {t('Media')}
                </Typography>

                <ImageUploadField
                    id={'image-upload-organization'}
                    image={organization.media}
                    setImage={(image) => updateField('media', image)}
                    ratio={'landscape'}
                />
            </Stack>

            <Stack gap={1} alignItems={'flex-start'}>
                <Typography variant={'subtitle1'} textAlign={'start'}>
                    {t('Logo')}
                </Typography>

                <ImageUploadField
                    id={'image-upload-organization-logo'}
                    image={organization.logo}
                    setImage={(image) => updateField('logo', image)}
                    width={230}
                    height={230}
                    ratio={'free'}
                    compact
                    supportSvg={true}
                />
            </Stack>
        </Stack>
    )

    const basicInfoSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Basic info')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display name shown across the dashboard and organization-level communication.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={organization.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={t('Contact email')}
                    tooltip={t('Organization email used for guest-facing contact details, including printouts.')}
                >
                    <TextField
                        id={'contact-email'}
                        type={'email'}
                        size={'small'}
                        value={organization.primaryEmail ?? ''}
                        onChange={(e) => updateField('primaryEmail', e.target.value)}
                        inputProps={{'aria-label': t('Contact email')}}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Contact phone')}
                    tooltip={t('Organization phone number used for guest-facing contact details, including printouts.')}
                >
                    <TextField
                        id={'guest-contact-phone'}
                        type={'tel'}
                        size={'small'}
                        value={organization.guestContactPhone ?? ''}
                        onChange={(e) => updateField('guestContactPhone', e.target.value)}
                        inputProps={{'aria-label': t('Contact phone')}}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Primary language')}
                    tooltip={t('Default language used when content has no specific translation.')}
                >
                    <Autocomplete
                        id={'language-code'}
                        options={[
                            undefined,
                            ...ISO_LANGS.map(lang => lang.code)
                        ]}
                        getOptionLabel={(option: any) => option ? getNameForLanguage(option) || option : '(Not set)'}
                        value={organization.primaryLanguage || ''}
                        fullWidth
                        onChange={(e, value) => {
                            updateField('primaryLanguage', value)
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                    />
                </Property>

                <Property
                    title={t('Additional languages')}
                    tooltip={t('Languages available in localized text fields and translation tools.')}
                >
                    <Autocomplete
                        id={'additional-languages'}
                        multiple
                        disableClearable
                        options={[
                            undefined,
                            ...ISO_LANGS.map(lang => lang.code.toLowerCase())
                        ]}
                        getOptionLabel={(option: any) => option ? getNameForLanguage(option) || option : '(Not set)'}
                        value={organization.supportedLanguages || []}
                        fullWidth
                        sx={{
                            '& .MuiAutocomplete-input': {
                                flexGrow: 0,
                                width: 0,
                                minWidth: 0,
                                p: '0 !important',
                            },
                        }}
                        onChange={(e, value) => {
                            updateField('supportedLanguages', value)
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                    />
                </Property>

                <Property
                    title={t('Currency code')}
                    tooltip={t('Default currency used for prices and billing amounts.')}
                >
                    <Autocomplete
                        id={'amount-currency'}
                        options={[
                            undefined,
                            ...Object.keys(CURRENCY_CODE_SYMBOLS)
                        ]}
                        getOptionLabel={(option: any) => option ? formatCurrencyNameSymbol(option) || '(Not set)' : '(Not set)'}
                        value={organization.currencyCode || ''}
                        fullWidth
                        onChange={(e, value) => {
                            updateField('currencyCode', value)
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        sx={{
                            width: '100%'
                        }}
                    />

                </Property>

                <OrganizationUrlsField
                    urls={organization.urls}
                    setUrls={(urls) => updateField('urls', urls)}
                />
            </Stack>

        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {organization.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const addressSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Address')}
            </Typography>

            <AddressField
                id={'address'}
                // Index 0 hardcoded because its expected that in org there is only one address (for now)
                index={0}
                value={organization.address}
                onChange={(e) => updateField('address', e)}
                gap={fieldGroupGap}
                timeZoneTooltip={t('Time zone used for organization dates, times, and scheduling defaults.')}
                addressTypeTooltip={t('Classifies the organization address for internal records and connected systems.')}
            />
        </Stack>
    )

    const legalSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Legal & banking details')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <Property
                    title={t('Legal name')}
                    tooltip={t('Registered company name, used where the legal entity has to be named instead of the display name.')}
                >
                    <TextField
                        id={'legalName'}
                        size={'small'}
                        value={organization.legalName ?? ''}
                        onChange={(e) => updateField('legalName', e.target.value)}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('VAT ID')}
                    tooltip={t('VAT identification number (UID), printed on invoices.')}
                >
                    <TextField
                        id={'vatId'}
                        size={'small'}
                        value={organization.vatId ?? ''}
                        onChange={(e) => updateField('vatId', e.target.value)}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Commercial register number')}
                    tooltip={t('Commercial register number (FN, Firmenbuchnummer), printed on invoices.')}
                >
                    <TextField
                        id={'commercialRegisterNumber'}
                        size={'small'}
                        value={organization.commercialRegisterNumber ?? ''}
                        onChange={(e) => updateField('commercialRegisterNumber', e.target.value)}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('IBAN')}
                    tooltip={t('Bank account number printed on invoices for bank transfers.')}
                >
                    <TextField
                        id={'iban'}
                        size={'small'}
                        value={organization.iban ?? ''}
                        onChange={(e) => updateField('iban', e.target.value)}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('BIC')}
                    tooltip={t('Bank identifier code (SWIFT) printed on invoices next to the IBAN.')}
                >
                    <TextField
                        id={'bic'}
                        size={'small'}
                        value={organization.bic ?? ''}
                        onChange={(e) => updateField('bic', e.target.value)}
                        fullWidth
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const agenciesSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Agencies')}
            </Typography>

            <Property
                title={t('Agencies with individual reservations')}
                tooltip={t('Accommodation services listed here stay as individual floor-plan reservations instead of being grouped.')}
            >
                <Autocomplete
                    multiple
                    disableClearable
                    freeSolo
                    id={'whitelist-agencies'}
                    options={[]}
                    value={organization.agenciesWithIndividualReservations || []}
                    onChange={(e, value) => updateField('agenciesWithIndividualReservations', value)}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option}
                                size={'small'}
                                {...getTagProps({index})}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size={'small'}
                            fullWidth
                            placeholder={t('Type agency name and press Enter')}
                        />
                    )}
                    sx={{width: '100%'}}
                />
            </Property>
        </Stack>
    )

    const publicKeySection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Access')}
            </Typography>

            <Property
                title={t('Public Key')}
                tooltip={t('Used by the booking widget embed to identify this organization.')}
            >
                <TextField
                    id={'publicKey'}
                    size={'small'}
                    value={organization.publicKey}
                    fullWidth
                    required
                    InputProps={{
                        endAdornment: <CopyAdornment value={organization.publicKey || ''}/>,
                    }}
                />
            </Property>
        </Stack>
    )

    //endregion UI

    const organizationDataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                mt: 3,
                gap: 6
            }}
        >
            {titleSection}
            {basicInfoSection}
            <Divider sx={sectionDividerSx}/>
            {addressSection}
            <Divider sx={sectionDividerSx}/>
            {legalSection}
            <Divider sx={sectionDividerSx}/>
            {agenciesSection}
            <Divider sx={sectionDividerSx}/>
            {publicKeySection}
            {dangerZoneSection}
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {organizationDataUI}
        </Box>
    );
}

export default OrganizationDetails;
